import { useState, useEffect } from 'react';
import { generateAutoGrid } from '../../services/AutomationsService';
import SelectSymbol from '../SelectSymbol';
import { showNotification } from '../../utils/notify';

/**
 * props:
 * - symbol (opcional, para contexto)
 * - onNotification
 */
function GridAIModal(props) {

    const [symbol, setSymbol] = useState(props.symbol || '');
    const [percentage, setPercentage] = useState(10);
    const [levels, setLevels] = useState(5);
    const [priceRange, setPriceRange] = useState(5);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Verificar permissão ao montar o componente
    useEffect(() => {
        const modal = document.getElementById('modalGridAI');
        if (modal) {
            const handleShow = () => {
                if (localStorage.getItem("hasGridAI") !== 'true') {
                    // Prevenir abertura do modal
                    const bsModal = window.bootstrap?.Modal?.getInstance(modal);
                    if (bsModal) {
                        bsModal.hide();
                    }
                    const limitName = localStorage.getItem("limitName") || 'Gold';
                    showNotification(`Função disponível apenas para usuários ${limitName} que têm esta função ativa.`, 'info');
                }
            };
            modal.addEventListener('show.bs.modal', handleShow);
            return () => {
                modal.removeEventListener('show.bs.modal', handleShow);
            };
        }
    }, []);

    function onSymbolChange(event) {
        setSymbol(event.target.value);
    }

    function onSubmit(event) {
        event.preventDefault();
        
        // Verificar permissão antes de submeter
        if (localStorage.getItem("hasGridAI") !== 'true') {
            const limitName = localStorage.getItem("limitName") || 'Gold';
            showNotification(`Função disponível apenas para usuários ${limitName} que têm esta função ativa.`, 'info');
            // Fechar o modal
            const modal = document.getElementById('modalGridAI');
            if (modal) {
                const bsModal = window.bootstrap?.Modal?.getInstance(modal);
                if (bsModal) {
                    bsModal.hide();
                }
            }
            return;
        }
        
        if (!symbol) {
            if (props.onNotification)
                props.onNotification({ type: 'error', text: 'Selecione um símbolo!' });
            return;
        }

        setLoading(true);
        setResult(null);

        generateAutoGrid(symbol, percentage, levels, priceRange)
            .then(data => {
                if (data && data.automation) {
                    setResult(data);
                    if (props.onNotification)
                        props.onNotification({ 
                            type: 'success', 
                            text: `Grid Trading criado com sucesso para ${symbol}! ${data.description || ''}` 
                        });
                } else {
                    throw new Error('Resposta inválida do servidor');
                }
            })
            .catch(err => {
                console.error('Erro ao criar grid:', err);
                const errorMessage = err.response?.data || err.message || 'Erro desconhecido ao criar grid';
                if (props.onNotification)
                    props.onNotification({ type: 'error', text: errorMessage });
            })
            .finally(() => setLoading(false));
    }

    function renderResult() {
        if (!result || !result.automation) return <></>;

        return (
            <div className="mt-3">
                <div className="alert alert-success">
                    <h6>Grid Trading Criado com Sucesso!</h6>
                    <p><b>Símbolo:</b> {result.automation?.symbol || 'N/A'}</p>
                    <p><b>Nome:</b> {result.automation?.name || 'N/A'}</p>
                    <p><b>Status:</b> {result.automation?.isActive ? 'Ativo' : 'Inativo'}</p>
                    {result.grid && (
                        <>
                            <p><b>Níveis:</b> {result.grid.levels}</p>
                            <p><b>Faixa de Preço:</b> ±{result.grid.priceRange}%</p>
                            <p><b>Limite Inferior:</b> {result.grid.lowerLimit?.toFixed(8)}</p>
                            <p><b>Limite Superior:</b> {result.grid.upperLimit?.toFixed(8)}</p>
                            <p><b>Quantidade por Grid:</b> {result.grid.quantity}</p>
                        </>
                    )}
                    <p className="mb-0"><b>Descrição:</b> {result.description || 'Grid criado com sucesso.'}</p>
                </div>
            </div>
        );
    }

    // Verificar permissão antes de renderizar
    const hasPermission = localStorage.getItem("hasGridAI") === 'true';
    
    if (!hasPermission) {
        // Não renderizar o modal se não tiver permissão
        return null;
    }

    return (
        <div className="modal fade" id="modalGridAI" tabIndex="-1" role="dialog" aria-labelledby="modalGridAILabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div className="modal-content bg-white">
                    <div className="modal-header">
                        <h5 className="modal-title" id="modalGridAILabel">IA - Criar Grid Trading</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <form onSubmit={onSubmit}>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Símbolo</label>
                                <SelectSymbol
                                    symbol={symbol}
                                    onChange={onSymbolChange}
                                    disabled={loading}
                                />
                                <div className="form-text">Escolha o par de moedas para o grid trading</div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Porcentagem de Investimento: <strong>{percentage}%</strong>
                                </label>
                                <input
                                    type="range"
                                    className="form-range"
                                    min="1"
                                    max="100"
                                    value={percentage}
                                    onChange={(e) => setPercentage(Number(e.target.value))}
                                    disabled={loading}
                                />
                                <div className="form-text">Percentual do saldo disponível a ser investido</div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Número de Níveis: <strong>{levels}</strong>
                                </label>
                                <input
                                    type="range"
                                    className="form-range"
                                    min="3"
                                    max="20"
                                    value={levels}
                                    onChange={(e) => setLevels(Number(e.target.value))}
                                    disabled={loading}
                                />
                                <div className="form-text">Quantidade de níveis de compra/venda no grid</div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Faixa de Preço: <strong>±{priceRange}%</strong>
                                </label>
                                <input
                                    type="range"
                                    className="form-range"
                                    min="1"
                                    max="20"
                                    value={priceRange}
                                    onChange={(e) => setPriceRange(Number(e.target.value))}
                                    disabled={loading}
                                />
                                <div className="form-text">Faixa de preço em relação ao preço atual (ex: ±5% = de -5% a +5%)</div>
                            </div>
                            {renderResult()}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" disabled={loading}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading || !symbol}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Criando...
                                    </>
                                ) : (
                                    'Criar Grid'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default GridAIModal;


